import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import type { FreezeFile } from '../models/freeze-reason.model';

export interface UploadedFreezeFile {
  file: File;
  base64: string;
  purpose_code: string;
  preview?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FreezeFileService {
  private translate = inject(TranslateService);

  readonly maxFileSize = 5 * 1024 * 1024; // 5MB
  readonly acceptedFileTypes = [
    '.pdf',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.doc',
    '.docx',
  ];

  /**
   * Validate a file for size and type.
   * Returns an error key string if invalid, null if valid.
   */
  validateFile(file: File): string | null {
    if (file.size > this.maxFileSize) {
      return 'FREEZE.FREEZE_SIDEBAR.FILE_SIZE_ERROR';
    }
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.acceptedFileTypes.includes(extension)) {
      return 'FREEZE.FREEZE_SIDEBAR.FILE_TYPE_ERROR';
    }
    return null;
  }

  /**
   * Process a file: validate, convert to base64, create preview if image.
   * Returns a Promise that resolves with the UploadedFreezeFile or rejects with an error key.
   */
  processFile(file: File, purposeCode: string): Promise<UploadedFreezeFile> {
    const validationError = this.validateFile(file);
    if (validationError) {
      return Promise.reject(validationError);
    }

    return new Promise<UploadedFreezeFile>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result as string;
        const preview = file.type.startsWith('image/')
          ? base64String
          : undefined;

        resolve({
          file,
          base64: base64String.split(',')[1],
          purpose_code: purposeCode,
          preview,
        });
      };

      reader.onerror = () => {
        reject('FREEZE.FREEZE_SIDEBAR.FILE_UPLOAD_ERROR');
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Prepare files array for API payload
   */
  prepareFilesForAPI(uploadedFiles: UploadedFreezeFile[]): FreezeFile[] {
    return uploadedFiles.map((f) => ({
      file_base64: f.base64,
      purpose_code: f.purpose_code,
    }));
  }

  /**
   * Get a FontAwesome icon class for a file based on its extension
   */
  getFileIcon(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf text-red-500';
      case 'doc':
      case 'docx':
        return 'fa-file-word text-blue-500';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fa-file-image text-green-500';
      default:
        return 'fa-file text-600';
    }
  }

  /**
   * Get file size in a human-readable format
   */
  getFileSize(file: File): string {
    const bytes = file.size;
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Check if a file is an image
   */
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }
}
